import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, useMotionValueEvent } from 'motion/react';
import { ArrowLeft, Clock, Eye, Calendar, Share2, Bookmark, ArrowRight, User, Linkedin, ChevronDown, ChevronUp, MessageCircle, RefreshCw, FileText } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import Layout from '../components/layout/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <motion.div 
    className="border-b border-[#FF6B35]/10 last:border-b-0"
    initial={false}
  >
    <button
      onClick={onClick}
      className="w-full py-4 flex items-center justify-between text-left"
    >
      <span className="text-[#2D2D2D] font-medium pr-4">{question}</span>
      {isOpen ? <ChevronUp size={20} className="text-[#FF6B35] flex-shrink-0" /> : <ChevronDown size={20} className="text-[#8A8A8A] flex-shrink-0" />}
    </button>
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="overflow-hidden"
    >
      <p className="pb-4 text-[#5A5A5A] leading-relaxed">{answer}</p>
    </motion.div>
  </motion.div>
);

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [author, setAuthor] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [schemas, setSchemas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFAQ, setOpenFAQ] = useState(0);
  const [readProgress, setReadProgress] = useState(0);

  const heroRef = useRef(null);
  const articleRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  // Reading progress tracker
  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      const el = articleRef.current;
      const rect = el.getBoundingClientRect();
      const totalHeight = el.scrollHeight;
      const visible = Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top);
      const scrolled = Math.max(0, -rect.top);
      const progress = Math.min(100, Math.max(0, (scrolled / (totalHeight - window.innerHeight)) * 100));
      setReadProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [post]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      // Try new CMS API first
      const response = await fetch(`${API_URL}/api/public/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data.post);
        setAuthor(data.author);
        setRelatedPosts(data.related_posts || []);
        setSchemas(data.schemas);
      } else {
        // Fallback to old API
        const oldResponse = await fetch(`${API_URL}/api/blog/posts/${slug}`);
        if (oldResponse.ok) {
          const data = await oldResponse.json();
          setPost(data);
        }
      }
    } catch (error) {
      console.error('Error fetching post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
          <motion.div
            className="w-12 h-12 border-2 border-[#FF6B35]/30 border-t-[#FF6B35] rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#FFFBF5] flex flex-col items-center justify-center">
          <h1 className="font-display text-4xl text-[#2D2D2D] mb-4">Article Not Found</h1>
          <p className="text-[#5A5A5A] mb-8">The article you are looking for does not exist.</p>
          <Link to="/blog" className="btn-sunrise">
            <span className="flex items-center gap-2">
              <ArrowLeft size={18} />
              Back to Blog
            </span>
          </Link>
        </div>
      </Layout>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const imageUrl = post?.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
  const readTime = post?.read_time || 5;
  const categoryDisplay = post?.category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Blog';
  const authorName = post?.author_name || author?.name || 'Buzz Craft Maven Team';

  return (
    <Layout>
      {/* SEO Meta Tags & Schema */}
      <Helmet>
        <title>{post.meta_title || post.title} | Buzz Craft Maven</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta name="keywords" content={post.keywords?.join(', ')} />
        <link rel="canonical" href={`https://buzzcraftmaven.com/blog/${post.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:image" content={imageUrl.startsWith('/') ? `https://buzzcraftmaven.com${imageUrl}` : imageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://buzzcraftmaven.com/blog/${post.slug}`} />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:modified_time" content={post.updated_at || post.published_at} />
        <meta property="article:author" content={authorName} />
        <meta property="article:section" content={categoryDisplay} />
        {post.tags?.map((tag, i) => <meta key={i} property="article:tag" content={tag} />)}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.meta_title || post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        <meta name="twitter:image" content={imageUrl.startsWith('/') ? `https://buzzcraftmaven.com${imageUrl}` : imageUrl} />
        
        {/* Schema.org JSON-LD */}
        {schemas?.blogPosting && (
          <script type="application/ld+json">
            {JSON.stringify(schemas.blogPosting)}
          </script>
        )}
        {schemas?.faq && (
          <script type="application/ld+json">
            {JSON.stringify(schemas.faq)}
          </script>
        )}
        {schemas?.breadcrumb && (
          <script type="application/ld+json">
            {JSON.stringify(schemas.breadcrumb)}
          </script>
        )}
        {/* SpeakableSpecification for voice search */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "speakable": {
              "@type": "SpeakableSpecification",
              "cssSelector": ["[data-testid='blog-title']", "[data-testid='blog-introduction']", "[data-testid='blog-expert-takeaway']"]
            },
            "url": `https://buzzcraftmaven.com/blog/${post.slug}`
          })}
        </script>
        {/* Organization Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Buzz Craft Maven",
            "url": "https://buzzcraftmaven.com",
            "sameAs": ["https://www.linkedin.com/company/buzzcraftmaven"]
          })}
        </script>
      </Helmet>

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-[60] h-1 bg-transparent">
        <motion.div
          className="h-full bg-gradient-to-r from-[#FF6B35] to-[#FFB627]"
          style={{ width: `${readProgress}%` }}
          data-testid="reading-progress-bar"
        />
      </div>

      {/* HERO SECTION */}
      <section ref={heroRef} className="relative min-h-[70vh] pt-32 md:pt-40 bg-sunrise-hero overflow-hidden flex items-end">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroY }}
        >
          <img
            src={imageUrl}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFBF5] via-[#FFFBF5]/80 to-transparent" />
        </motion.div>

        <div className="absolute top-20 right-[20%] w-[400px] h-[400px] sun-orb sun-orb-primary opacity-30" />

        <div className="container mx-auto px-6 md:px-12 py-16 relative z-10">
          <nav className="mb-8 text-sm" aria-label="Breadcrumb" data-testid="breadcrumb-nav">
            <ol className="flex items-center gap-2 text-[#8A8A8A]" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/" className="hover:text-[#FF6B35] transition-colors" itemProp="item"><span itemProp="name">Home</span></Link>
                <meta itemProp="position" content="1" />
              </li>
              <span>/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to="/blog" className="hover:text-[#FF6B35] transition-colors" itemProp="item"><span itemProp="name">Insights</span></Link>
                <meta itemProp="position" content="2" />
              </li>
              <span>/</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <Link to={`/blog/category/${post.category}`} className="hover:text-[#FF6B35] transition-colors" itemProp="item"><span itemProp="name">{categoryDisplay}</span></Link>
                <meta itemProp="position" content="3" />
              </li>
              <span>/</span>
              <li className="text-[#2D2D2D] truncate max-w-xs" itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <span itemProp="name">{post.title?.substring(0, 50)}{post.title?.length > 50 ? '...' : ''}</span>
                <meta itemProp="position" content="4" />
              </li>
            </ol>
          </nav>

          <div className="max-w-4xl">
            <TextReveal>
              <div className="flex items-center gap-3 mb-6 flex-wrap">
                <Link to={`/blog/category/${post.category}`}>
                  <span className="inline-block px-4 py-1.5 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-sm font-semibold rounded-full hover:shadow-lg transition-shadow">
                    {categoryDisplay}
                  </span>
                </Link>
                {post.is_pillar && (
                  <span className="px-3 py-1 bg-purple-500 text-white text-xs font-semibold rounded-full">
                    Comprehensive Guide
                  </span>
                )}
                {post.is_recently_updated && (
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full flex items-center gap-1" data-testid="recently-updated-badge">
                    <RefreshCw size={10} /> Recently Updated
                  </span>
                )}
              </div>
            </TextReveal>

            <TextReveal delay={0.1}>
              <h1 className="font-display text-3xl md:text-4xl lg:text-5xl text-[#2D2D2D] leading-tight mb-6" data-testid="post-title">
                {post.title}
              </h1>
            </TextReveal>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap items-center gap-4 md:gap-6 text-[#5A5A5A]"
            >
              <Link to={author ? `/blog/author/${author.slug}` : '#'} className="flex items-center gap-2 hover:text-[#FF6B35] transition-colors">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white font-semibold">
                  {authorName.charAt(0)}
                </div>
                <span className="text-[#2D2D2D] font-medium">{authorName}</span>
              </Link>
              <span className="flex items-center gap-1" data-testid="published-date">
                <Calendar size={16} className="text-[#FF6B35]" />
                {formatDate(post.published_at)}
              </span>
              {post.updated_at && post.updated_at !== post.published_at && (
                <span className="flex items-center gap-1 text-emerald-600" data-testid="last-updated-date">
                  <RefreshCw size={14} />
                  Updated {formatDate(post.updated_at)}
                </span>
              )}
              <span className="flex items-center gap-1" data-testid="read-time">
                <Clock size={16} className="text-[#FF6B35]" />
                {readTime} min read
              </span>
              {post.word_count > 0 && (
                <span className="flex items-center gap-1" data-testid="word-count">
                  <FileText size={14} className="text-[#FF6B35]" />
                  {post.word_count.toLocaleString()} words
                </span>
              )}
              <span className="flex items-center gap-1">
                <Eye size={16} className="text-[#FF6B35]" />
                {post.views || 0} views
              </span>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ARTICLE CONTENT */}
      <section ref={articleRef} className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8 lg:gap-16">
            {/* Sidebar - Share & Actions */}
            <div className="col-span-12 lg:col-span-1 lg:sticky lg:top-32 lg:h-fit">
              <div className="flex lg:flex-col items-center gap-4 pb-8 lg:pb-0 border-b lg:border-b-0 border-[#FF6B35]/10">
                <button 
                  onClick={() => navigator.share?.({ title: post.title, url: window.location.href })}
                  className="w-10 h-10 rounded-full border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-all"
                >
                  <Share2 size={18} />
                </button>
                <button className="w-10 h-10 rounded-full border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-[#FF6B35] hover:border-[#FF6B35] transition-all">
                  <Bookmark size={18} />
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="col-span-12 lg:col-span-8">
              {/* Introduction Section */}
              {post.introduction && (
                <div className="mb-8 p-6 bg-gradient-to-r from-[#FFFBF5] to-white rounded-2xl border-l-4 border-[#FF6B35]">
                  <p className="text-lg text-[#5A5A5A] leading-relaxed italic">{post.introduction}</p>
                </div>
              )}

              {/* Key Insights */}
              {post.key_insights && post.key_insights.length > 0 && (
                <div className="mb-10 p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                  <h2 className="text-lg font-display font-semibold text-[#2D2D2D] mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white text-sm">✓</span>
                    Key Takeaways
                  </h2>
                  <ul className="space-y-3">
                    {post.key_insights.filter(i => i).map((insight, index) => (
                      <li key={index} className="flex items-start gap-3 text-[#5A5A5A]">
                        <span className="text-[#FF6B35] font-bold mt-0.5">{index + 1}.</span>
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Main Content */}
              <article className="prose prose-lg max-w-none" data-testid="post-content">
                <ReactMarkdown
                  components={{
                    h1: ({children}) => <h1 className="text-3xl font-display font-bold text-[#2D2D2D] mt-8 mb-4">{children}</h1>,
                    h2: ({children}) => <h2 className="text-2xl font-display font-semibold text-[#2D2D2D] mt-8 mb-4">{children}</h2>,
                    h3: ({children}) => <h3 className="text-xl font-display font-semibold text-[#2D2D2D] mt-6 mb-3">{children}</h3>,
                    p: ({children}) => <p className="text-[#5A5A5A] text-lg leading-relaxed mb-4">{children}</p>,
                    ul: ({children}) => <ul className="list-disc list-inside text-[#5A5A5A] space-y-2 mb-4 ml-4">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal list-inside text-[#5A5A5A] space-y-2 mb-4 ml-4">{children}</ol>,
                    li: ({children}) => <li className="text-[#5A5A5A] text-lg">{children}</li>,
                    blockquote: ({children}) => <blockquote className="border-l-4 border-[#FF6B35] pl-4 italic text-[#5A5A5A] my-6">{children}</blockquote>,
                    em: ({children}) => <em className="text-[#5A5A5A] italic">{children}</em>,
                    strong: ({children}) => <strong className="text-[#2D2D2D] font-semibold">{children}</strong>,
                    hr: () => <hr className="border-[#FF6B35]/20 my-8" />,
                    a: ({href, children}) => <a href={href} className="text-[#FF6B35] hover:underline">{children}</a>,
                    table: ({children}) => <div className="overflow-x-auto my-6"><table className="min-w-full border border-[#FF6B35]/20 rounded-lg">{children}</table></div>,
                    th: ({children}) => <th className="bg-[#FFFBF5] px-4 py-2 text-left text-[#2D2D2D] font-semibold border-b border-[#FF6B35]/20">{children}</th>,
                    td: ({children}) => <td className="px-4 py-2 text-[#5A5A5A] border-b border-[#FF6B35]/10">{children}</td>,
                    code: ({children}) => <code className="bg-[#FFFBF5] px-2 py-1 rounded text-[#FF6B35] text-sm">{children}</code>,
                    pre: ({children}) => <pre className="bg-[#2D2D2D] text-white p-4 rounded-lg overflow-x-auto my-4">{children}</pre>,
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </article>

              {/* Expert Takeaway */}
              {post.expert_takeaway && (
                <div className="mt-10 p-6 bg-gradient-to-br from-purple-50 to-white rounded-2xl border border-purple-200">
                  <h3 className="text-lg font-display font-semibold text-[#2D2D2D] mb-3 flex items-center gap-2">
                    <span className="text-purple-600">💡</span> Expert Takeaway
                  </h3>
                  <p className="text-[#5A5A5A] leading-relaxed">{post.expert_takeaway}</p>
                </div>
              )}

              {/* Conclusion */}
              {post.conclusion && (
                <div className="mt-10 p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                  <h3 className="text-lg font-display font-semibold text-[#2D2D2D] mb-3">In Summary</h3>
                  <p className="text-[#5A5A5A] leading-relaxed">{post.conclusion}</p>
                </div>
              )}

              {/* FAQ Section */}
              {post.faq_section && post.faq_section.length > 0 && post.faq_section.some(f => f.question && f.answer) && (
                <div className="mt-12 p-6 bg-white rounded-2xl border border-[#FF6B35]/10" id="faq">
                  <h2 className="text-2xl font-display font-semibold text-[#2D2D2D] mb-6 flex items-center gap-3">
                    <MessageCircle className="text-[#FF6B35]" size={24} />
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-0">
                    {post.faq_section.filter(f => f.question && f.answer).map((faq, index) => (
                      <FAQItem
                        key={index}
                        question={faq.question}
                        answer={faq.answer}
                        isOpen={openFAQ === index}
                        onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Section */}
              {post.cta_text && (
                <div className="mt-12 p-8 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] rounded-2xl text-white text-center">
                  <p className="text-lg mb-4">{post.cta_text}</p>
                  <Link to={post.cta_link || '/contact'}>
                    <motion.button
                      className="px-8 py-3 bg-white text-[#FF6B35] rounded-full font-semibold shadow-xl hover:shadow-2xl transition-shadow"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started
                    </motion.button>
                  </Link>
                </div>
              )}

              {/* Keywords */}
              {post.keywords && post.keywords.length > 0 && (
                <div className="mt-12 pt-8 border-t border-[#FF6B35]/10">
                  <p className="text-[#8A8A8A] text-sm uppercase tracking-widest mb-4">Related Topics</p>
                  <div className="flex flex-wrap gap-2">
                    {post.keywords.map((keyword, index) => (
                      <Link key={index} to={`/blog?search=${encodeURIComponent(keyword)}`}>
                        <span className="px-3 py-1 bg-[#FFFBF5] text-[#5A5A5A] text-sm rounded-full border border-[#FF6B35]/10 hover:border-[#FF6B35]/30 transition-colors">
                          {keyword}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Box */}
              <div className="mt-12 p-6 md:p-8 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white font-display text-2xl font-bold flex-shrink-0">
                    {authorName.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-[#2D2D2D] font-semibold text-lg mb-1">{authorName}</h4>
                        <p className="text-[#8A8A8A] text-sm">
                          {author?.expertise?.slice(0, 3).join(' • ') || 'Digital Marketing Expert'}
                        </p>
                      </div>
                      {author?.linkedin_url && (
                        <a href={author.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#0077B5] hover:opacity-80">
                          <Linkedin size={20} />
                        </a>
                      )}
                    </div>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed mt-3">
                      {author?.bio || 'Expert insights from our collective of specialists in digital marketing, design, photography, and technology.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-span-12 lg:col-span-3">
              <div className="lg:sticky lg:top-32 space-y-6">
                {/* Table of Contents (for pillar content) */}
                {post.is_pillar && (
                  <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/10">
                    <h4 className="text-[#2D2D2D] font-semibold mb-4">On This Page</h4>
                    <nav className="space-y-2 text-sm">
                      {post.key_insights?.length > 0 && <a href="#" className="block text-[#5A5A5A] hover:text-[#FF6B35]">Key Takeaways</a>}
                      <a href="#" className="block text-[#5A5A5A] hover:text-[#FF6B35]">Main Content</a>
                      {post.faq_section?.length > 0 && <a href="#faq" className="block text-[#5A5A5A] hover:text-[#FF6B35]">FAQ</a>}
                    </nav>
                  </div>
                )}

                {/* CTA Card */}
                <div className="p-6 bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 rounded-2xl border border-[#FF6B35]/20">
                  <h4 className="text-[#2D2D2D] font-semibold mb-4">Need Help?</h4>
                  <p className="text-[#5A5A5A] text-sm mb-4">
                    Our specialists can help you implement these strategies for your business.
                  </p>
                  <Link to="/contact" className="btn-sunrise text-sm px-4 py-2 w-full justify-center">
                    <span>Get in Touch</span>
                  </Link>
                </div>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className="p-6 bg-white rounded-2xl border border-[#FF6B35]/10">
                    <h4 className="text-[#2D2D2D] font-semibold mb-4">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, i) => (
                        <Link key={i} to={`/blog/tag/${tag}`}>
                          <span className="px-3 py-1 bg-[#FFFBF5] text-[#5A5A5A] text-xs rounded-full hover:bg-[#FF6B35]/10 transition-colors">
                            #{tag}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED POSTS */}
      {relatedPosts.length > 0 && (
        <section className="section-padding bg-[#FFFBF5]">
          <div className="container mx-auto px-6 md:px-12">
            <TextReveal>
              <div className="flex items-center justify-between mb-12">
                <h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D]">
                  More <span className="text-gradient-sunrise">Articles</span>
                </h2>
                <Link to="/blog" className="btn-ghost-warm">
                  View All
                  <ArrowRight size={16} />
                </Link>
              </div>
            </TextReveal>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost, index) => {
                const relatedImageUrl = relatedPost.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
                return (
                <TextReveal key={relatedPost.id || relatedPost.slug} delay={index * 0.1}>
                  <Link to={`/blog/${relatedPost.slug}`}>
                    <motion.article
                      className="card-fresh overflow-hidden group"
                      whileHover={{ y: -8 }}
                    >
                      <div className="relative h-40 -mx-8 -mt-8 mb-4 overflow-hidden">
                        <img
                          src={relatedImageUrl}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                      </div>
                      <span className="text-[#FF6B35] text-xs uppercase tracking-widest font-medium">
                        {relatedPost.category?.replace(/-/g, ' ')}
                      </span>
                      <h3 className="font-display text-lg text-[#2D2D2D] mt-2 group-hover:text-[#FF6B35] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                    </motion.article>
                  </Link>
                </TextReveal>
              );
              })}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default BlogPost;
